import { camelize } from '@ember/string';
import { tracked } from '@glimmer/tracking';

import { attr, belongsTo, hasMany } from '@warp-drive/legacy/model';
import { trackedFunction } from 'reactiveweb/function';

import Model from 'umg-ember-backend/models/variant-model';
import {
  buildValidations,
  disabledWhenVariant,
  disabledWhenVariantOrNotNew,
  validator,
} from 'umg-ember-backend/utils/validations';

const Validations = buildValidations({
  assetFolderId: [
    validator('presence', {
      presence: true,
      disabled: disabledWhenVariant(),
    }),
    validator('number', {
      allowString: true,
      integer: true,
    }),
  ],
  headline: validator('presence', {
    presence: true,
  }),
  tmpAssetName: validator('presence', {
    presence: true,
    disabled: disabledWhenVariantOrNotNew(),
    messageKey: 'forms.asset.messages.tmpAssetName',
  }),
});

export const settings = {
  copyableOptions: {
    ignoreAttributes: [
      'channelsAdditive',
      'channelsExcluded',
      'channelsExclusive',
      'ownedByChannel',
    ],
  },
  saveHooks: {
    after: [
      async function saveNewAssetFormatRelations(model) {
        const assetFormatRelations = await model.assetFormatRelations;
        const newRecords = assetFormatRelations.filter((record) => {
          return record.isNew;
        });

        await Promise.all(newRecords.map((record) => record.save()));

        return model;
      },
    ],
  },
  variantProperties: [
    'cmsLanguageId',
    'cmsState',
    'copyright',
    'headline',
    'seoTitle',
    'seoDescription',
    'keywords',
    'seoImage',
    'subHeadline',
    'text',
    'versiontitle',
  ],
};

export default class AssetModel extends Model.extend(Validations) {
  // Attributes
  @attr('string') assetFolderId;
  @attr('string') assetTypeSelector;
  @attr changeDate;
  @attr('string') copyright;
  @attr('string') director;
  @attr('string') headline;
  @attr('string') imageUrlTemplate;
  @attr('string') recordPartnerId;
  @attr('string') signedVideoUrl;
  @attr('string') subHeadline;
  @attr('string') text;
  @attr('string') tmpAssetName;
  @attr('string') versiontitle;
  @attr('string') videoIsrc;
  @attr('string') videoUrl;

  // CMS Fields
  @attr('number', { defaultValue: 2 }) cmsState;
  @attr('string') seoTitle;
  @attr('string') seoDescription;
  @attr('string') keywords;

  // Meta fields
  @attr cmsCreated;
  @attr cmsDeleted;
  @attr cmsLocktime;
  @attr cmsModified;
  @attr() cmsHistory;
  @belongsTo('user', { async: true, inverse: null }) cmsOwnerId;
  @belongsTo('user', { async: true, inverse: null }) cmsCreatorId;
  @belongsTo('user', { async: true, inverse: null }) cmsLockerId;
  @belongsTo('user', { async: true, inverse: null }) cmsModifierId;

  // Relations
  @hasMany('artist', { async: true, inverse: null }) artists;
  @hasMany('asset-format-relation', { async: true, inverse: 'asset' })
  assetFormatRelations;
  @hasMany('asset-token-config', { async: true, inverse: 'assets' })
  assetTokenConfigs;
  @hasMany('channel', { async: true, inverse: null }) channelsAdditive;
  @hasMany('channel', { async: true, inverse: null }) channelsExcluded;
  @hasMany('channel', { async: true, inverse: null }) channelsExclusive;
  @belongsTo('asset', { async: true, inverse: null }) seoImage;
  @belongsTo('channel', { async: true, inverse: null }) ownedByChannel;
  @hasMany('product', { async: true, inverse: null }) products;
  @hasMany('taxon', { async: true, inverse: null }) taxa;

  //  Defaults
  @tracked newPreviewTime = null;

  // Getter and setter
  get assetType() {
    const assetTypeSelector = this.assetTypeSelector;

    if (!assetTypeSelector) {
      return null;
    }

    return camelize(assetTypeSelector);
  }

  get displayHeadline() {
    return this.headline;
  }

  get displayLabel() {
    return `${this.headline} (${this.id})`;
  }

  get isAudio() {
    return this.assetType === 'audio';
  }

  get hasError() {
    const relations = this.resolvedAssetFormatRelations.value || [];

    return relations.some((r) => r.hasError);
  }

  get isDocument() {
    return this.assetType === 'document';
  }

  get isImage() {
    return this.assetType === 'image';
  }

  get isVideo() {
    return this.assetType === 'video';
  }

  get sourceFormat() {
    const relations = this.resolvedAssetFormatRelations.value || [];

    return relations.find((r) => r.isSourceFormat);
  }

  resolvedAssetFormatRelations = trackedFunction(this, async () => {
    return await this.assetFormatRelations;
  });
}
